import styled from '@emotion/styled';
import { FormControl, Tooltip, Typography } from '@material-ui/core';
import Help from '@material-ui/icons/Help';
import { createElement, FC } from 'react';

import { DynamicSchemaDefinitionSection } from '../../../../generated/graphql';
import theme from '../../../../theme';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';

type Props = {
  section: DynamicSchemaDefinitionSection;
  className?: string;
};

const Section: FC<Props> = ({ section, className, children }) => (
  <Root className={className}>
    <Title variant="subtitle1">
      {section.label}
      {section.meta && section.meta.helperUrlForDirector ? (
        section.meta.helperTooltipForDirector ? (
          <Tooltip title={section.meta.helperTooltipForDirector} placement="top">
            <HelpAnchor href={section.meta.helperUrlForDirector} target="_blank" rel="noopener noreferrer">
              <HelpIcon />
            </HelpAnchor>
          </Tooltip>
        ) : (
          <HelpAnchor href={section.meta.helperUrlForDirector} target="_blank" rel="noopener noreferrer">
            <HelpIcon />
          </HelpAnchor>
        )
      ) : null}
    </Title>
    <Description variant="body1">{section.description}</Description>
    <Details>
      <Fields>{children}</Fields>
    </Details>
  </Root>
);

const Root = styled(FormControl)`
  margin-bottom: ${SPACING_REGULAR}px;
`;

const Title = styled(Typography)`
  ${gridLayoutItem('section_title')}
  font-weight: 700;
`;

const Description = styled(Typography)`
  ${gridLayoutItem('description')}
  margin-top: ${SPACING_UNIT}px;
`;

const Details = styled.div`
  ${gridLayoutContainer({
    areas: [['description'], ['fields']],
    rows: ['auto', 'auto'],
    columns: ['100%'],
  })}
  margin-top: ${SPACING_REGULAR}px;
  padding: ${SPACING_REGULAR}px;
  background: ${theme.palette.background.default};
`;

const Fields = styled.div`
  ${gridLayoutItem('fields')}
  ${flexibleLayoutContainer({ direction: 'column' })}
  & > * {
    margin-bottom: ${SPACING_UNIT * 5}px;
  }

  & > *:last-child {
    margin-bottom: ${SPACING_REGULAR}px;
  }
`;

const HelpAnchor = styled.a`
  display: inline-flex;
  align-items: center;
  vertical-align: -0.3rem;
  margin-left: ${SPACING_UNIT}px;
  color: ${theme.palette.secondary.main};
  text-decoration: none;
`;

const HelpIcon = styled(Help)`
  margin-right: 2px;
  font-size: 20px;
`;

export default Section;
